import * as Blockly from 'blockly/core';
import { defineMutableBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import { FieldStyledLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledLabel';

const COLOR = CATEGORIES.control.color;
const ICON_ADD = { src: '/images/block/btn_add.svg', size: 20 };
const ICON_REMOVE = { src: '/images/block/btn_remove.svg', size: 20 };

function genId(): string {
    return Math.random().toString(36).slice(2, 8);
}

function getElseIfIds(block: Blockly.Block): string[] {
    return (block as any)._elseIfIds as string[];
}

function attachShadowIfEmpty(block: Blockly.Block, inputName: string): void {
    const input = block.getInput(inputName);
    if (!input?.connection || input.connection.targetBlock()) return;
    const shadow = (block.workspace as Blockly.WorkspaceSvg).newBlock('bool');
    shadow.setShadow(true);
    shadow.outputConnection?.connect(input.connection);
    requestAnimationFrame(() => {
        shadow.initSvg();
        shadow.render();
    });
}

function appendElseIfInputs(block: Blockly.Block, id: string): void {
    block.appendValueInput(`IF_${id}`).setCheck('Boolean').appendField('でなければ もし');

    block
        .appendDummyInput(`LABEL_DO_${id}`)
        .appendField('なら')
        .appendField(
            new Blockly.FieldImage(ICON_REMOVE.src, ICON_REMOVE.size, ICON_REMOVE.size, '-', function (
                this: Blockly.FieldImage,
            ) {
                requestAnimationFrame(() => removeElseIf(block, id));
            }),
            `REMOVE_${id}`,
        );

    block.appendStatementInput(`DO_${id}`);
}

function appendElseInputs(block: Blockly.Block): void {
    block
        .appendDummyInput('LABEL_ELSE')
        .appendField('でなければ')
        .appendField(
            new Blockly.FieldImage(ICON_REMOVE.src, ICON_REMOVE.size, ICON_REMOVE.size, '-', function (
                this: Blockly.FieldImage,
            ) {
                requestAnimationFrame(() => removeElse(block));
            }),
            'REMOVE_ELSE',
        );

    block.appendStatementInput('ELSE');
}

function addClause(block: Blockly.Block): void {
    const hasElse = (block as any)._hasElse as boolean;

    if (!hasElse) {
        (block as any)._hasElse = true;
        appendElseInputs(block);
    } else {
        const id = genId();
        const ids = getElseIfIds(block);

        const elseBlock = block.getInput('ELSE')?.connection?.targetBlock() ?? null;
        block.removeInput('LABEL_ELSE');
        block.removeInput('ELSE');

        ids.push(id);
        appendElseIfInputs(block, id);
        attachShadowIfEmpty(block, `IF_${id}`);

        appendElseInputs(block);

        if (elseBlock) {
            const conn = block.getInput('ELSE')?.connection;
            const tc = elseBlock.previousConnection;
            if (conn && tc && !tc.isConnected()) tc.connect(conn);
        }
    }

    (block.workspace as Blockly.WorkspaceSvg).render();
}

function removeElseIf(block: Blockly.Block, id: string): void {
    const ifTarget = block.getInput(`IF_${id}`)?.connection?.targetBlock();
    const doTarget = block.getInput(`DO_${id}`)?.connection?.targetBlock();

    if (ifTarget && !ifTarget.isShadow()) ifTarget.dispose(true);

    if (doTarget) {
        let current: Blockly.Block | null = doTarget;
        while (current) {
            const next: Blockly.Block | null = current.getNextBlock();
            current.dispose(true);
            current = next;
        }
    }

    block.removeInput(`IF_${id}`);
    block.removeInput(`LABEL_DO_${id}`);
    block.removeInput(`DO_${id}`);

    const ids = getElseIfIds(block);
    const idx = ids.indexOf(id);
    if (idx !== -1) ids.splice(idx, 1);

    (block.workspace as Blockly.WorkspaceSvg).render();
}

function removeElse(block: Blockly.Block): void {
    const doTarget = block.getInput('ELSE')?.connection?.targetBlock();

    if (doTarget) {
        let current: Blockly.Block | null = doTarget;
        while (current) {
            const next: Blockly.Block | null = current.getNextBlock();
            current.dispose(true);
            current = next;
        }
    }

    block.removeInput('LABEL_ELSE');
    block.removeInput('ELSE');
    (block as any)._hasElse = false;

    (block.workspace as Blockly.WorkspaceSvg).render();
}

defineMutableBlock({
    type: 'if',
    colour: COLOR,
    tooltip: '条件に応じて処理を分岐します',
    movable: true,
    deletable: true,
    previousStatement: true,
    nextStatement: true,

    init(this: Blockly.Block) {
        const block = this;
        (this as any)._elseIfIds = [] as string[];
        (this as any)._hasElse = false;
        this.setColour(COLOR);
        this.setTooltip('条件に応じて処理を分岐します');
        this.setMovable(true);
        this.setDeletable(true);
        this.setPreviousStatement(true);
        this.setNextStatement(true);

        this.appendValueInput('IF0')
            .setCheck('Boolean')
            .appendField(new FieldStyledLabel('IF', { fontSize: 12, fontWeight: 'bold' }))
            .appendField(' もし');

        this.appendDummyInput('LABEL_DO0')
            .appendField('なら')
            .appendField(
                new Blockly.FieldImage(ICON_ADD.src, ICON_ADD.size, ICON_ADD.size, '+', function (
                    this: Blockly.FieldImage,
                ) {
                    requestAnimationFrame(() => addClause(block));
                }),
                'ADD',
            );

        this.appendStatementInput('DO0');

        attachShadowIfEmpty(this, 'IF0');
    },

    mutationToDom(this: Blockly.Block): Element {
        const container = Blockly.utils.xml.createElement('mutation');
        container.setAttribute('elseif', getElseIfIds(this).join(','));
        container.setAttribute('else', (this as any)._hasElse ? '1' : '0');
        return container;
    },

    domToMutation(this: Blockly.Block, xml: Element): void {
        const raw = xml.getAttribute('elseif') ?? '';
        const ids = raw ? raw.split(',') : [];
        const hasElse = xml.getAttribute('else') === '1';
        (this as any)._elseIfIds = ids;
        (this as any)._hasElse = hasElse;
        for (const id of ids) {
            appendElseIfInputs(this, id);
        }
        if (hasElse) appendElseInputs(this);
    },
});
